import { Button } from '@/components/ui/button.js';
import { Calendar } from '@/components/ui/calendar.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { ArrowTooltip } from '@/components/arrow-tooltip.js';
import { formatSelectedDateRangeLabel } from '@/lib/student-utils.js';
import { cn } from '@/lib/tailwind-utils.js';
import { localDateFromPlainDate, plainDateFromLocalDate } from '@/lib/time-zones.js';
import { Check, Info, PlusCircle } from 'lucide-react';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { Temporal } from 'temporal-polyfill';

export interface TableFilterAppliedPlainDateRangePickerProps {
  fromPlainDateString?: string;
  toPlainDateString?: string;
  onRangeApply: (range: { fromPlainDateString?: string; toPlainDateString?: string }) => void;
  placeholder?: string;
  helperText?: string;
  infoTooltip?: string;
  buttonTestId?: string;
}

function toDraftRange(fromPlainDateString?: string, toPlainDateString?: string): DateRange | undefined {
  if (!fromPlainDateString && !toPlainDateString) {
    return undefined;
  }
  const fromString = fromPlainDateString ?? toPlainDateString!;
  const toString = toPlainDateString ?? fromPlainDateString!;
  return {
    from: localDateFromPlainDate(Temporal.PlainDate.from(fromString)),
    to: localDateFromPlainDate(Temporal.PlainDate.from(toString)),
  };
}

function normalizeAppliedRange(range: DateRange | undefined): {
  fromPlainDateString?: string;
  toPlainDateString?: string;
} {
  if (!range?.from) {
    return {};
  }
  const fromPlainDateString = plainDateFromLocalDate(range.from).toString();
  const toPlainDateString = range.to ? plainDateFromLocalDate(range.to).toString() : fromPlainDateString;
  return { fromPlainDateString, toPlainDateString };
}

export const TableFilterAppliedPlainDateRangePicker = ({
  fromPlainDateString,
  toPlainDateString,
  onRangeApply,
  placeholder = 'Date range',
  helperText,
  infoTooltip = 'Select dates to view attended sessions.',
  buttonTestId,
}: TableFilterAppliedPlainDateRangePickerProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(() =>
    toDraftRange(fromPlainDateString, toPlainDateString),
  );

  const hasAppliedRange = Boolean(fromPlainDateString || toPlainDateString);
  const appliedLabel =
    hasAppliedRange && fromPlainDateString
      ? `Date range: ${formatSelectedDateRangeLabel(fromPlainDateString, toPlainDateString ?? fromPlainDateString)}`
      : hasAppliedRange && toPlainDateString
        ? `Date range: ${formatSelectedDateRangeLabel(toPlainDateString)}`
        : placeholder;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftRange(toDraftRange(fromPlainDateString, toPlainDateString));
    }
    setIsCalendarOpen(open);
  };

  const handleApply = () => {
    onRangeApply(normalizeAppliedRange(draftRange));
    setIsCalendarOpen(false);
  };

  const handleClear = () => {
    setDraftRange(undefined);
    onRangeApply({});
    setIsCalendarOpen(false);
  };

  const calendarClasses = {
    caption_label: 'text-sm font-semibold',
    head_cell: 'text-muted-foreground rounded-md w-7 mx-0.5 font-normal text-[0.7rem]',
    cell: cn(
      'h-7 w-7 text-center text-[0.7rem] mx-0.5 py-0 relative focus-within:relative focus-within:z-20',
      '[&:has([aria-selected].day-range-start)]:bg-transparent',
      '[&:has([aria-selected].day-range-end)]:bg-transparent',
      '[&:has([aria-selected].day-range-middle)]:bg-[#FDE8EA]',
      '[&:has([aria-selected].day-range-middle)]:rounded-sm',
    ),
    day: cn(
      'h-7 w-7 p-0 font-normal inline-flex items-center justify-center rounded-sm',
      'border border-foreground/30 bg-background text-foreground',
      'hover:bg-muted aria-selected:opacity-100',
    ),
    day_selected: cn(
      'bg-foreground !text-white border-foreground/30 rounded-sm',
      'hover:bg-foreground hover:!text-white focus:bg-foreground focus:!text-white',
    ),
    day_range_start: 'day-range-start rounded-sm border border-foreground/30 !text-white',
    day_range_end: 'day-range-end rounded-sm border border-foreground/30 !text-white',
    day_range_middle: cn(
      'aria-selected:bg-[#FDE8EA] aria-selected:!text-foreground aria-selected:border aria-selected:border-foreground/30',
      'rounded-sm',
    ),
    day_outside: 'text-muted-foreground opacity-50 border-foreground/30',
    day_today: 'font-normal',
  };

  return (
    <div className="flex items-center gap-1.5">
      <Popover open={isCalendarOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('h-8 border-dashed', hasAppliedRange && 'border-solid')}
            data-testid={buttonTestId}
            aria-label={hasAppliedRange ? appliedLabel : placeholder}
          >
            {hasAppliedRange ? (
              <Check className="mr-2 h-4 w-4" aria-hidden />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" aria-hidden />
            )}
            <span>{appliedLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden rounded-md border p-0 shadow-md" align="start">
          <Calendar
            mode="range"
            numberOfMonths={1}
            selected={draftRange}
            onSelect={setDraftRange}
            defaultMonth={draftRange?.from}
            initialFocus
            classNames={calendarClasses}
          />
          <div className="space-y-2 border-t px-3 py-3">
            {draftRange?.from ? (
              <p className="text-sm font-medium text-foreground">
                {formatSelectedDateRangeLabel(
                  plainDateFromLocalDate(draftRange.from).toString(),
                  draftRange.to
                    ? plainDateFromLocalDate(draftRange.to).toString()
                    : plainDateFromLocalDate(draftRange.from).toString(),
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Select one or multiple days</p>
            )}
            {helperText ? <p className="text-xs italic text-muted-foreground">{helperText}</p> : null}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 border-foreground/20 bg-background text-foreground hover:bg-muted"
                onClick={handleClear}
                data-testid={buttonTestId ? `${buttonTestId}-clear` : undefined}
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8 bg-foreground text-background hover:bg-foreground/90"
                disabled={!draftRange?.from}
                onClick={handleApply}
                data-testid={buttonTestId ? `${buttonTestId}-apply` : undefined}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {infoTooltip ? (
        <ArrowTooltip
          position="top"
          trigger={<Info className="h-4 w-4 text-muted-foreground" aria-label={infoTooltip} />}
          contentClassName="bg-black/80 before:border-t-black/80"
        >
          {infoTooltip}
        </ArrowTooltip>
      ) : null}
    </div>
  );
};
